/**
 * BranchPositionPage — "Can this branch pay?" (BT Batch 2 P2, owner ask).
 * Per-branch financial position: inter-branch owes/owed, wallet balances,
 * this month's earnings, and recent settlements. Data:
 * GET /cross-branch-payments/branch-position/{branch_id}.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP, fmtDateTime } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { RefreshCw, TrendingUp, TrendingDown, Wallet, Scale } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BranchPositionPage() {
  const { user, branches, effectiveBranchId } = useAuth();
  const [branchId, setBranchId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!branchId) {
      const first = effectiveBranchId && effectiveBranchId !== 'all'
        ? effectiveBranchId : (branches?.[0]?.id || '');
      if (first) setBranchId(first);
    }
  }, [branches, effectiveBranchId, branchId]);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const res = await api.get(`${API}/api/cross-branch-payments/branch-position/${branchId}`);
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const net = data?.net_position || 0;

  return (
    <div className="p-4 space-y-4" data-testid="branch-position-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>Branch Financial Position</h1>
          <p className="text-xs text-slate-500 mt-0.5">Inter-branch debts, wallets and earnings — the "can this branch pay?" view</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="h-9 w-52" data-testid="bp-branch-select"><SelectValue placeholder="Pick a branch" /></SelectTrigger>
            <SelectContent>
              {(branches || []).map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid="bp-refresh">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {!data ? (
        <div className="text-sm text-slate-400 py-16 text-center">{loading ? 'Loading…' : 'Pick a branch to see its position.'}</div>
      ) : (
        <>
          {/* ── Headline cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            <Card className={net >= 0 ? 'border-emerald-200' : 'border-rose-200'}>
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 text-xs uppercase font-semibold text-slate-500"><Scale size={13} />Net Inter-Branch Position</div>
                <div className={`text-2xl font-bold ${net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="bp-net">{formatPHP(net)}</div>
                <div className="text-xs text-slate-500">{net >= 0 ? 'Other branches owe it more than it owes' : 'It owes other branches more than it is owed'}</div>
              </CardContent>
            </Card>
            <Card className="border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 text-xs uppercase font-semibold text-amber-700"><TrendingDown size={13} />Owes (unsettled)</div>
                <div className="text-2xl font-bold text-slate-800" data-testid="bp-owes">{formatPHP(data.owes.total)}</div>
                <div className="text-xs text-slate-500">{data.owes.count} collection(s) held for other branches</div>
              </CardContent>
            </Card>
            <Card className="border-teal-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 text-xs uppercase font-semibold text-teal-700"><TrendingUp size={13} />Owed (unsettled)</div>
                <div className="text-2xl font-bold text-slate-800" data-testid="bp-owed">{formatPHP(data.owed.total)}</div>
                <div className="text-xs text-slate-500">{data.owed.count} collection(s) held by other branches</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 text-xs uppercase font-semibold text-slate-500"><Wallet size={13} />This Month</div>
                <div className={`text-2xl font-bold ${data.month.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="bp-month-net">{formatPHP(data.month.net)}</div>
                <div className="text-xs text-slate-500">Sales {formatPHP(data.month.sales)} − expenses {formatPHP(data.month.expenses)}</div>
              </CardContent>
            </Card>
          </div>

          {/* ── Wallets strip ── */}
          <Card>
            <CardContent className="p-4 flex flex-wrap gap-4">
              <span className="text-xs uppercase font-semibold text-slate-500 flex items-center gap-1.5"><Wallet size={13} />Wallets</span>
              {(data.wallets || []).length === 0 ? (
                <span className="text-xs text-slate-400">No wallets recorded for this branch</span>
              ) : data.wallets.map((w, i) => (
                <span key={i} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
                  <span className="text-slate-500">{w.name || w.type}:</span>{' '}
                  <b className={parseFloat(w.balance) < 0 ? 'text-rose-700' : 'text-slate-800'}>{formatPHP(w.balance)}</b>
                </span>
              ))}
            </CardContent>
          </Card>

          {/* ── Per-counterpart breakdown ── */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 text-xs font-semibold text-amber-800">Owes to (unsettled, by branch)</div>
                <Table>
                  <TableBody>
                    {data.owes.by_branch.length === 0 ? (
                      <TableRow><TableCell className="text-center text-xs text-slate-400 py-6">Nothing owed — clean slate</TableCell></TableRow>
                    ) : data.owes.by_branch.map(b => (
                      <TableRow key={b.branch_id}>
                        <TableCell className="text-sm">{b.branch_name} <span className="text-[10px] text-slate-400">({b.count})</span></TableCell>
                        <TableCell className="text-right text-sm font-bold text-amber-700">{formatPHP(b.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-2.5 bg-teal-50 border-b border-teal-100 text-xs font-semibold text-teal-800">Owed by (unsettled, by branch)</div>
                <Table>
                  <TableBody>
                    {data.owed.by_branch.length === 0 ? (
                      <TableRow><TableCell className="text-center text-xs text-slate-400 py-6">No one owes this branch</TableCell></TableRow>
                    ) : data.owed.by_branch.map(b => (
                      <TableRow key={b.branch_id}>
                        <TableCell className="text-sm">{b.branch_name} <span className="text-[10px] text-slate-400">({b.count})</span></TableCell>
                        <TableCell className="text-right text-sm font-bold text-teal-700">{formatPHP(b.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* ── Recent settlements ── */}
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-700">Recent Settlements</div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-xs uppercase text-slate-500">ST #</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Route</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Mode</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-right">Amount</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Verified</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">When</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(data.recent_settlements || []).length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-xs text-slate-400 py-6">No settlements yet</TableCell></TableRow>
                  ) : data.recent_settlements.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{s.st_number}</TableCell>
                      <TableCell className="text-xs">{s.collecting_branch_name} → {s.credit_branch_name}</TableCell>
                      <TableCell className="text-xs">{s.mode === 'documented' ? 'Bank transfer' : 'Internal'}</TableCell>
                      <TableCell className="text-right text-xs font-semibold">{formatPHP(s.total_amount)} <span className="text-[10px] text-slate-400">({s.payment_count})</span></TableCell>
                      <TableCell>
                        {s.admin_verified
                          ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Verified</Badge>
                          : <Badge className="text-[10px] bg-amber-100 text-amber-800">Pending verify</Badge>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{fmtDateTime(s.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
